// Push Notification Service for Webinar Reminders
// Uses the Web Notifications API for browser notifications

class NotificationService {
  constructor() {
    this.permission = Notification.permission;
    this.scheduledNotifications = new Map();
  }

  // Check if notifications are supported
  isSupported() {
    return 'Notification' in window;
  }

  // Request permission for notifications
  async requestPermission() {
    if (!this.isSupported()) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    if (this.permission === 'granted') {
      return true;
    }

    if (this.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    }

    return false;
  }

  // Get current permission status
  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  }

  // Show immediate notification
  showNotification(title, options = {}) {
    if (!this.isSupported() || this.permission !== 'granted') {
      console.warn('Cannot show notification: permission not granted');
      return null;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      notification.onclick = () => {
        window.focus();
        if (options.url) {
          window.location.href = options.url;
        }
        notification.close();
      };

      return notification;
    } catch (error) {
      console.error('Error showing notification:', error);
      return null;
    }
  }

  // Schedule a notification for a specific time
  scheduleNotification(id, title, options, scheduledTime) {
    // Cancel existing notification with same ID
    this.cancelScheduledNotification(id);

    const now = new Date();
    const targetTime = new Date(scheduledTime);
    const delay = targetTime - now;

    if (delay <= 0) {
      // Time has passed, show immediately if within last hour
      if (delay > -3600000) {
        this.showNotification(title, options);
      }
      return null;
    }

    const timeoutId = setTimeout(() => {
      this.showNotification(title, options);
      this.scheduledNotifications.delete(id);
    }, delay);

    this.scheduledNotifications.set(id, {
      timeoutId,
      title,
      options,
      scheduledTime
    });

    return id;
  }

  // Cancel a scheduled notification
  cancelScheduledNotification(id) {
    const scheduled = this.scheduledNotifications.get(id);
    if (scheduled) {
      clearTimeout(scheduled.timeoutId);
      this.scheduledNotifications.delete(id);
      return true;
    }
    return false;
  }

  // Cancel all scheduled notifications
  cancelAllScheduledNotifications() {
    this.scheduledNotifications.forEach((scheduled, id) => {
      clearTimeout(scheduled.timeoutId);
    });
    this.scheduledNotifications.clear();
  }

  // Schedule webinar reminder notifications
  scheduleWebinarReminders(webinar, webinarUrl) {
    if (!webinar.date) return [];

    const webinarDate = new Date(webinar.date);
    const scheduledIds = [];

    // Reminder 1 day before
    const oneDayBefore = new Date(webinarDate);
    oneDayBefore.setDate(oneDayBefore.getDate() - 1);
    if (oneDayBefore > new Date()) {
      const id = this.scheduleNotification(
        `webinar-${webinar.id}-1day`,
        '📅 Webinar mañana',
        {
          body: `"${webinar.title}" comienza mañana a las ${webinarDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`,
          tag: `webinar-${webinar.id}`,
          url: webinarUrl
        },
        oneDayBefore
      );
      if (id) scheduledIds.push(id);
    }

    // Reminder 1 hour before
    const oneHourBefore = new Date(webinarDate);
    oneHourBefore.setHours(oneHourBefore.getHours() - 1);
    if (oneHourBefore > new Date()) {
      const id = this.scheduleNotification(
        `webinar-${webinar.id}-1hour`,
        '⏰ Webinar en 1 hora',
        {
          body: `"${webinar.title}" comienza en 1 hora. ¡Prepárate!`,
          tag: `webinar-${webinar.id}`,
          url: webinarUrl,
          requireInteraction: true
        },
        oneHourBefore
      );
      if (id) scheduledIds.push(id);
    }

    // Reminder 15 minutes before
    const fifteenMinBefore = new Date(webinarDate);
    fifteenMinBefore.setMinutes(fifteenMinBefore.getMinutes() - 15);
    if (fifteenMinBefore > new Date()) {
      const id = this.scheduleNotification(
        `webinar-${webinar.id}-15min`,
        '🔴 ¡Webinar en 15 minutos!',
        {
          body: `"${webinar.title}" está por comenzar. ¡Entra ahora a la sala de espera!`,
          tag: `webinar-${webinar.id}`,
          url: webinarUrl,
          requireInteraction: true
        },
        fifteenMinBefore
      );
      if (id) scheduledIds.push(id);
    }

    // Notification when webinar starts
    const id = this.scheduleNotification(
      `webinar-${webinar.id}-start`,
      '🎬 ¡El webinar ha comenzado!',
      {
        body: `"${webinar.title}" está en vivo ahora. ¡No te lo pierdas!`,
        tag: `webinar-${webinar.id}`,
        url: webinarUrl,
        requireInteraction: true
      },
      webinarDate
    );
    if (id) scheduledIds.push(id);

    return scheduledIds;
  }

  // Get scheduled notifications count
  getScheduledCount() {
    return this.scheduledNotifications.size;
  }
}

// Export singleton instance
const notificationService = new NotificationService();
export default notificationService;
